import * as React from "react";
import { Loader2, LogOut, ShieldAlert } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import type { SupportSessionView } from "@eduviaos/shared";

interface Props {
  session: SupportSessionView;
  onEnd: () => Promise<void>;
  onExpired: () => void;
}

/**
 * Phase SC-22 — persistent, non-dismissable banner shown for the whole of a
 * super-admin support session. Sits in normal flow above the app shell so
 * it never covers page content, and uses the theme's warning role.
 */
export function SupportSessionBanner({ session, onEnd, onExpired }: Props) {
  const expiresAt = React.useMemo(() => new Date(session.expires_at).getTime(), [session]);
  const [remainingMs, setRemainingMs] = React.useState(() => expiresAt - Date.now());
  const [ending, setEnding] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // Publish the banner's height so the shell's sticky sidebar, topbar and
  // page headers pin below it instead of sliding underneath.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--app-top-inset", `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty("--app-top-inset");
    };
  }, []);

  React.useEffect(() => {
    const tick = () => {
      const left = expiresAt - Date.now();
      setRemainingMs(left);
      if (left <= 0) onExpired();
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, onExpired]);

  const endTime = new Date(expiresAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      ref={ref}
      role="status"
      data-testid="support-session-banner"
      className="bg-warning text-warning-foreground border-warning-foreground/15 sticky top-0 z-50 border-b"
    >
      <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 sm:px-4">
        <ShieldAlert className="size-4 shrink-0" aria-hidden="true" />
        <p className="min-w-0 flex-1 basis-56 text-[13px] leading-snug">
          <span className="font-semibold">EduViaOS support view</span> — you are signed in to{" "}
          <span className="font-semibold">{session.tenant_name}</span> as{" "}
          <span className="break-all font-medium">{session.sa_email}</span>. Full access. Every
          change is recorded.
          <span className="sr-only"> Session ends at {endTime}.</span>
        </p>
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            title={`Ends at ${endTime}`}
            className="bg-warning-foreground/10 rounded-md px-2 py-1 font-mono text-xs tabular-nums"
          >
            {formatRemaining(remainingMs)}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={ending}
            onClick={() => {
              setEnding(true);
              void onEnd();
            }}
            className="border-warning-foreground/30 text-warning-foreground hover:bg-warning-foreground/10 h-8 bg-transparent transition-transform duration-150 ease-out active:scale-[0.97] motion-reduce:transition-none"
          >
            {ending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <LogOut className="size-3.5" aria-hidden="true" />
            )}
            End session
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
